import { Analytics } from '@analytics';
import { fetchGroupByCardId, fetchGroupByGuideId } from '@api/taptotip/group';
import ConfirmIcon from '@assets/images/ConfirmIcon.svg';
import EmojiAvatar from '@components/EmojiAvatar';
import TopNav from '@components/Nav/TopNav';
import GroupCard from '@components/TapToTip/Groups/GroupCard';
import GroupDetailsCard from '@components/TapToTip/Groups/GroupDetailsCard';
import MobileConfirmationDialog from '@components/TapToTip/MobileConfirmationDialog';
import { GroupEntityType } from '@constants';
import { Button, CircularProgress } from '@material-ui/core';
import { AccountCircle as AccountIcon } from '@material-ui/icons';
import { useInjectSaga } from '@utils/injectSaga';
import { isMerchantOfRecordCountry } from '@utils/helpers';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import * as actions from './actions';
import messages from './messages';
import saga from './saga';

const cardClasses = 'bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6';

const primaryButtonClasses =
  'w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer !normal-case disabled:bg-slate-300 disabled:text-white disabled:cursor-not-allowed';

const GroupPage = ({ addToGroup, user }) => {
  const intl = useIntl();
  // Merchant-of-record guides (US, Canada) cannot join a team.
  const isUS = isMerchantOfRecordCountry(user?.profile?.country);
  useInjectSaga({ key: 'tipPayments', saga });
  const { cardId, guideId } = useParams();
  const [groupData, setGroupData] = useState(null);
  const [isUserInGroup, setIsUserInGroup] = useState(null);
  const [description, setDescription] = useState('');
  const [buttons, setButtons] = useState(null);
  const [isConfirmationDialogShown, setIsConfirmationDialogShown] = useState(false);
  const [dialogIcon, setDialogIcon] = useState(null);

  useEffect(() => {
    if (user && groupData && groupData.group) {
      setIsUserInGroup(!!groupData.group.users.find(groupUser => groupUser._id === user.userId));
    }
  }, [user, groupData]);

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        if (cardId || guideId) {
          const data = cardId
            ? await fetchGroupByCardId(cardId)
            : await fetchGroupByGuideId(guideId);
          setGroupData(data);
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchGroupData();
  }, [cardId, guideId, user]);

  if (!groupData) {
    return (
      <center>
        <CircularProgress />
      </center>
    );
  }

  const handleAddToTeam = () => {
    Analytics.track('clicked add to team button');
    addToGroup({
      groupId: user.groupDetails._id,
      type: GroupEntityType.user,
      id: groupData.user._id
    });
  };

  const handleJoinTeamWithGuide = () => {
    Analytics.track('clicked create team with user button');
    addToGroup({
      type: GroupEntityType.user,
      id: groupData.user._id
    });
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationDialogShown(false);
  };

  const showAddToTeamDialog = ({ type }) => {
    setDialogIcon(<img alt="confirm" src={ConfirmIcon} />);
    setDescription(
      user.groupDetails
        ? intl.formatMessage(messages.claimResourceInGroup)
        : intl.formatMessage(messages.claimResourceConfirmation)
    );

    setButtons([
      <Button
        className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer !normal-case"
        key="confirm-button"
        onClick={() => {
          Analytics.track('confirm add to team', {
            type: GroupEntityType.resource
          });
          addToGroup({
            type,
            id: type === GroupEntityType.resource ? groupData.resource._id : groupData.user._id,
            action : type === GroupEntityType.resource ?'claim': null,
          });
        }}>
        {intl.formatMessage(messages.claimResource)}
      </Button>,
      <Button
        className="mt-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2.5 rounded-xl !border !border-solid !border-slate-300 transition-colors cursor-pointer !normal-case"
        key="cancel-button"
        onClick={closeConfirmationDialog}>
        {intl.formatMessage(messages.cancel)}
      </Button>
    ]);

    setIsConfirmationDialogShown(true);
  };
  const renderInfoCard = type => {
    let properties = {};

    if (!groupData.group) {
      if (type === GroupEntityType.resource) {
        properties = {
          icon: groupData.resource.icon,
          title: groupData.resource.name,
          description: intl.formatMessage(messages.resourceNotPartOfTeam)
        };
      }

      if (!groupData.group && type === GroupEntityType.resource && user.groupDetails) {
        properties = {
          icon: groupData.resource.icon,
          title: groupData.resource.name,
          description: intl.formatMessage(messages.addResourceToTeam)
        };
      }

      if (!groupData.group && type === GroupEntityType.user && !user.groupDetails._id) {
        properties = {
          icon: groupData.user.profile.avatarUrl,
          title: groupData.user.profile.firstName,
          description:
            groupData.user._id === user.userId
              ? intl.formatMessage(messages.youOwnThisCard)
              : intl.formatMessage(messages.createNewTeamWithUser)
        };
      }

      if (!groupData.group && type === GroupEntityType.user && user.groupDetails?._id) {
        properties = {
          icon: groupData.user.profile.avatarUrl,
          title: groupData.user.profile.firstName,
          description: intl.formatMessage(messages.addUserToTeam)
        };
      }
    }

    if (groupData.group) {
      if (type === GroupEntityType.resource) {
        properties = {
          icon: groupData.resource.icon,
          title: groupData.resource.name,
          description: intl.formatMessage(messages.createNewTeamWithResource)
        };
      }

      if (type === GroupEntityType.resource && user.groupDetails) {
        properties = {
          icon: groupData.resource.icon,
          title: groupData.resource.name,
          description: intl.formatMessage(messages.moveResourceToTeam)
        };
      }

      if (type === GroupEntityType.resource && user.groupDetails && isUserInGroup) {
        properties = {
          icon: groupData.resource.icon,
          title: groupData.resource.name,
          description: intl.formatMessage(messages.resourceIsPartOfTeam)
        };
      }

      if (type === GroupEntityType.user && !user.groupDetails) {
        properties = {
          icon: groupData.user.profile.avatarUrl,
          title: groupData.user.firstName,
          description: intl.formatMessage(messages.moveUserToNewTeam)
        };
      }

      if (type === GroupEntityType.user && user.groupDetails) {
        properties = {
          icon: groupData.user.profile.avatarUrl,
          title: groupData.user.profile.firstName,
          description: intl.formatMessage(messages.moveUserToExistingTeam)
        };
      }

      if (type === GroupEntityType.user && user.groupDetails && isUserInGroup) {
        properties = {
          icon: groupData.user.profile.avatarUrl,
          title: groupData.user.profile.firstName,
          description: intl.formatMessage(messages.userIsPartOfTeam)
        };
      }
    }

    return (
      <div className={`${cardClasses} mb-6`}>
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 grain rounded-3xl p-6 text-white shadow-xl shadow-indigo-200 relative overflow-hidden group flex flex-col justify-center items-center text-center min-h-[200px]">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          {type === GroupEntityType.user && groupData.user.profile.avatarUrl && (
            <img
              className="flex h-[60px] w-[60px] justify-center items-center rounded-full object-cover relative z-10"
              alt="Group icon"
              src={properties.icon}
            />
          )}
          {type === GroupEntityType.user && !groupData.user.profile.avatarUrl && (
            <AccountIcon className="flex h-[60px] w-[60px] text-[60px] justify-center items-center relative z-10" />
          )}
          {type === GroupEntityType.resource && (
            <div className="flex h-[60px] w-[60px] justify-center items-center relative z-10">
              <EmojiAvatar code={groupData.resource.emoji || ':credit_card:'} />
            </div>
          )}
          <div className="text-lg font-bold text-white mt-2 relative z-10">{properties.title}</div>
          <div className="text-sm text-indigo-100 font-medium mt-1 relative z-10">
            {properties.description}
          </div>
        </div>
        {!isUserInGroup && type !== GroupEntityType.user && (
          <div className="flex justify-center flex-col items-center">
            <Button
              variant="contained"
              className={primaryButtonClasses}
              onClick={() => {
                Analytics.track('click add to team', {
                  type: GroupEntityType.resource
                });
                showAddToTeamDialog({ type });
              }}>
              {`${type === GroupEntityType.resource ? 'Claim ':intl.formatMessage(messages.add)} `}
              {type === GroupEntityType.resource
                ? groupData.resource.name
                : groupData.user.profile.firstName}
              {` ${type === GroupEntityType.resource ? 'For My Team' :intl.formatMessage(messages.toMyTeam)}`}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TopNav />
      <div className="w-full m-auto p-2 sm:max-w-[400px] sm:p-4">
        {/* card belongs to another user that is not in a group */}
        {!groupData.group && groupData.user && groupData.user._id !== user.userId && (
          <>
            {renderInfoCard(GroupEntityType.user)}
            <div className={cardClasses}>
              <GroupDetailsCard
                type={GroupEntityType.user}
                isAGroup={false}
                isUserInGroup={isUserInGroup}
                elements={[groupData.user]}
                user={user}
              />
            </div>
          </>
        )}

        {/* the card belogs to the current user */}
        {!groupData.group && groupData.user && groupData.user._id === user.userId && (
          <>
            {renderInfoCard(GroupEntityType.user)}
            <div className={cardClasses}>
              <GroupDetailsCard
                type={GroupEntityType.user}
                isAGroup={false}
                isUserInGroup={false}
                elements={[groupData.user]}
                user={user}
              />
            </div>
          </>
        )}

        {/* card belongs to a resource that is not in a group */}
        {!groupData.group && groupData.resource && (
          <>
            {renderInfoCard(GroupEntityType.resource)}

            <div className={cardClasses}>
              <GroupDetailsCard
                isAGroup={false}
                type={GroupEntityType.resource}
                isUserInGroup={isUserInGroup}
                elements={[groupData.resource]}
                user={user}
              />
            </div>
          </>
        )}

        {/* card belongs to an element in an existing group */}
        {groupData.group && (
          <>
            {renderInfoCard(groupData.resource ? GroupEntityType.resource : GroupEntityType.user)}
            <div className={cardClasses}>
              <GroupCard
                group={groupData.group}
                cardOwnerType={groupData.resource ? GroupEntityType.resource : GroupEntityType.user}
                cardOwnerElement={groupData.resource ? groupData.resource : groupData.user}
                user={user}
                isUserInGroup={isUserInGroup}
                defaultOpenState={true}
                setDialogIcon={setDialogIcon}
                setDescription={setDescription}
                setButtons={setButtons}
                setIsConfirmationDialogShown={setIsConfirmationDialogShown}
                closeConfirmationDialog={closeConfirmationDialog}
              />
            </div>
          </>
        )}

        {/* card belongs to a user not already in a team while active user has a team */}
        {!groupData.group &&
          groupData.user &&
          groupData.user._id !== user.userId &&
          user.groupDetails &&
          !isUS && (
            <Button key="add-button" className={primaryButtonClasses} onClick={() => handleAddToTeam()}>
              {intl.formatMessage(messages.addUserToTeamButton)}
            </Button>
          )}

        {/* card belongs to a user not already in a team while active user does not have a team */}
        {!groupData.group &&
          groupData.user &&
          groupData.user._id !== user.userId &&
          !user.groupDetails &&
          !isUS && (
            <Button
              key="join-with-button"
              className={primaryButtonClasses}
              onClick={() => {
                handleJoinTeamWithGuide();
              }}>
              {intl.formatMessage(messages.createNewTeam)}
            </Button>
          )}
      </div>
      <MobileConfirmationDialog
        isShown={isConfirmationDialogShown}
        buttons={buttons}
        description={description}
        close={closeConfirmationDialog}
        icon={dialogIcon}
      />
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  addToGroup: info => dispatch(actions.addToGroup(info))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GroupPage);
